// ─────────────────────────────────────────────────────────────────────────────
// RiskDiscipline
//
// Sub-tab of the Analytics page. Shows risk-discipline detectors — overtrading,
// position size adherence, drawdown proximity, stop adherence, R-multiples.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";
import { useDetectors } from "../../hooks/useDetectors";
import { useUserRules } from "../../hooks/useUserRules";
import { useViewport } from "../../hooks/useViewport";
import { InsufficientStatePanel, EarlySignalBanner } from "../../components/DetectorProgress";

const CONFIDENCE_COLOR = {
  strong:       C.green,
  moderate:     C.yellow,
  thin:         C.red,
  insufficient: C.muted,
};
const CONFIDENCE_LABEL = {
  strong:       "Strong",
  moderate:     "Moderate",
  thin:         "Thin data",
  insufficient: "Needs data",
};

export function RiskDiscipline({ trades = [], profile, userId, accountId }) {
  const { rules: accountRules = [] } = useUserRules(userId, accountId);
  const { data: riskResult, loading: detectorsLoading, error: detectorsError } = useDetectors({
    suite: "risk",
    trades,
    profile,
    rules: accountRules,
    userId,
  });
  const detectors = riskResult?.detectors ?? [];
  const total_monthly_cost = riskResult?.total_monthly_cost ?? 0;

  const [expandedId, setExpandedId] = useState(null);

  // Check if we should show the "fill in stops" banner
  const showStopsBanner = useMemo(() => {
    const withStops = trades.filter(t =>
      t.stop_loss != null && t.stop_loss !== "" &&
      !isNaN(Number(t.stop_loss)) && Number(t.stop_loss) !== 0
    );
    const pctWithStops = trades.length > 0 ? withStops.length / trades.length : 0;
    return trades.length >= 5 && pctWithStops < 0.20;
  }, [trades]);

  if (detectorsLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: "#8892a4", fontSize: 14 }}>
        <span style={{ marginRight: 10 }}>⚙️</span> Analyzing patterns…
      </div>
    );
  }

  if (detectorsError && detectorsError !== "subscription_required") {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#ff6b6b", fontSize: 13 }}>
        Error loading detectors: {detectorsError}
      </div>
    );
  }

    if (!trades.length) {
    return (
      <Card>
        <SectionTitle>Risk Discipline</SectionTitle>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
          No trades yet. Once you have a few trading days logged, we'll show you
          where your discipline holds and where it slips.
        </p>
      </Card>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <p style={{ color: C.sub, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
          The most actionable category of trading data: where you said you'd do one
          thing and did another. Discipline costs nothing extra — and yet it's the
          biggest gap most traders never close.
        </p>
      </div>

      {showStopsBanner && <StopsNudgeBanner />}

      {total_monthly_cost > 0 && (
        <Card style={{ marginBottom: 16, background: C.red + "0a", border: `1px solid ${C.red}33` }}>
          <div style={{ fontSize: 10, color: C.red, letterSpacing: "0.1em", fontWeight: 700 }}>
            COST OF DISCIPLINE GAPS
          </div>
          <div style={{ fontSize: 36, fontWeight: 900, color: C.red, fontFamily: "monospace", marginTop: 4 }}>
            -${total_monthly_cost.toFixed(0)}<span style={{ fontSize: 16, color: C.muted, fontWeight: 600 }}> / month</span>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
            Estimated impact of overtrading, size violations, and drawdown overshoots — money left on the table
            from execution slippage rather than strategy failure.
          </div>
        </Card>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {detectors.map(d => (
          <DetectorCard
            key={d.id}
            detector={d}
            expanded={expandedId === d.id}
            onToggle={() => setExpandedId(expandedId === d.id ? null : d.id)}
          />
        ))}
      </div>

      <Disclaimer />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BANNER: "Fill in stops" nudge
// ─────────────────────────────────────────────────────────────────────────────

function StopsNudgeBanner() {
  return (
    <Card style={{ marginBottom: 16, background: C.blue + "11", border: `1px solid ${C.blue}55`, padding: 14 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ fontSize: 22 }}>💡</div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.blue, marginBottom: 4 }}>
            Unlock Stop Adherence + R-Multiples
          </div>
          <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6 }}>
            When you log trades, fill in <code style={{ color: C.text, fontFamily: "monospace" }}>stop_loss</code>
            {" "}— even retroactively. We'll then show you when you blew your stop (typically 30-40% of losses)
            and your R-multiple distribution. These are the single most actionable insights in any trade journal.
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DETECTOR CARD (reuses pattern from BehavioralPatterns)
// ─────────────────────────────────────────────────────────────────────────────

function DetectorCard({ detector, expanded, onToggle }) {
  const { isMobile } = useViewport();
  const d = detector;
  const isInsufficient = d.confidence === "insufficient";

  return (
    <Card style={{ padding: 0, overflow: "hidden", border: `1px solid ${C.border}` }}>
      <div
        onClick={onToggle}
        style={{
          padding: "14px 16px",
          cursor: "pointer",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
          gap: 16,
          alignItems: "center",
          transition: "background 0.1s",
          opacity: isInsufficient ? 0.65 : 1,
        }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{d.title}</span>
            <ConfidenceBadge level={d.confidence} n={d.n} />
          </div>
          {d.confidence === "thin" && <EarlySignalBanner detector={d} />}
          <div style={{ fontSize: 12.5, color: C.sub, lineHeight: 1.5 }}>
            {d.summary}
          </div>
        </div>
        <div style={{ textAlign: isMobile ? "left" : "right", minWidth: isMobile ? 0 : 120 }}>
          {d.estimated_cost > 0 ? (
            <>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.red, fontFamily: "monospace" }}>
                -${d.estimated_cost.toFixed(0)}
              </div>
              <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                /{d.cost_period}
              </div>
            </>
          ) : isInsufficient ? (
            <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>
              fill data to unlock
            </div>
          ) : (
            <div style={{ fontSize: 11, color: C.green, fontStyle: "italic" }}>
              disciplined
            </div>
          )}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            {expanded ? "▾ collapse" : "▸ details"}
          </div>
        </div>
      </div>

      {/* Insufficient-data state: progress bar + what's coming. Sits outside
          the click-toggle area so it doesn't trigger expand on tap. */}
      {isInsufficient && <InsufficientStatePanel detector={d} />}

      {expanded && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 16px", background: "rgba(0,0,0,0.2)" }}>
          {d.id === "rule_violations"     && <RuleViolationsDetails details={d.details} />}
          {d.id === "overtrading"         && <OvertradingDetails details={d.details} />}
          {d.id === "size_adherence"      && <SizeAdherenceDetails details={d.details} />}
          {d.id === "drawdown_proximity"  && <DrawdownDetails details={d.details} />}
          {d.id === "stop_adherence"      && <StopAdherenceDetails details={d.details} isInsufficient={isInsufficient} />}
          {d.id === "r_multiples"         && <RMultipleDetails details={d.details} isInsufficient={isInsufficient} />}
        </div>
      )}
    </Card>
  );
}

function ConfidenceBadge({ level, n }) {
  const color = CONFIDENCE_COLOR[level];
  const label = CONFIDENCE_LABEL[level];
  return (
    <span style={{
      fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700,
      color, padding: "2px 6px", borderRadius: 4,
      background: color + "1f", border: `1px solid ${color}55`,
    }}>
      {label} · n={n}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DETAIL VIEWS
// ─────────────────────────────────────────────────────────────────────────────

function RuleViolationsDetails({ details }) {
  const { rule_stats, total_violations } = details;

  if (!rule_stats || rule_stats.length === 0) {
    return <Empty msg="No rule violations recorded yet. Set up rules in Settings to start tracking deviations from your own plan." />;
  }

  const maxCount = Math.max(...rule_stats.map(s => s.count), 1);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Violations by rule
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rule_stats.map((s, i) => {
            const widthPct = (s.count / maxCount) * 100;
            const isMostCommon = i === 0;
            return (
              <div key={s.rule_type} style={{
                padding: "10px 12px",
                background: "rgba(0,0,0,0.25)",
                borderRadius: 6,
                border: `1px solid ${isMostCommon ? C.red + "33" : C.border}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                    {s.label}
                  </span>
                  <span style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>
                    {s.count}× • {fmt$(s.total_net)} total
                    {s.cost > 0 && (
                      <span style={{ color: C.red, marginLeft: 6 }}>
                        cost ~{fmt$(-s.cost)}
                      </span>
                    )}
                  </span>
                </div>
                <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{
                    width: `${widthPct}%`, height: "100%",
                    background: isMostCommon ? C.red : C.yellow,
                    transition: "width 0.3s",
                  }} />
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                  Win rate on violations: {(s.win_rate * 100).toFixed(0)}% • Baseline: {fmt$(s.baseline_avg)} avg
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Aggregated reflection notes */}
      {rule_stats.some(s => s.notes && s.notes.length > 0) && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            What you wrote at the time
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {rule_stats.flatMap(s =>
              (s.notes || []).map((n, i) => ({
                ...n,
                rule: s.label,
                key: `${s.rule_type}-${i}`,
              }))
            ).slice(0, 8).map(n => (
              <div key={n.key} style={{
                padding: "8px 10px",
                background: "rgba(0,0,0,0.2)",
                borderRadius: 4,
                fontSize: 12,
                lineHeight: 1.5,
              }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>
                  {n.date} • {n.rule}
                </div>
                <div style={{ color: C.text, fontStyle: "italic" }}>
                  "{n.note}"
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Advice>
        {total_violations >= 5
          ? `Your most expensive deviation is "${rule_stats[0]?.label}" — happened ${rule_stats[0]?.count}× and cost roughly ${fmt$(-rule_stats[0]?.cost || 0)}. The fix isn't analysis, it's discipline: which rule, if you held perfectly for 30 days, would change your account the most?`
          : `You've broken your own rules ${total_violations} time${total_violations === 1 ? "" : "s"} so far. Keep tracking — patterns become clear once you have 10+ violations to compare.`}
      </Advice>
    </div>
  );
}

function OvertradingDetails({ details }) {
  const { correlation, median_count, light_days, heavy_days, scatter_data } = details;
  if (!scatter_data || scatter_data.length === 0) {
    return <Empty msg="Not enough days to analyze." />;
  }

  return (
    <div>
      <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <StatBlock
          label={`Light days (≤${median_count} trades)`}
          rows={[
            ["Days", light_days.count],
            ["Avg P&L", fmt$(light_days.avg_pnl)],
          ]}
          accentColor={light_days.avg_pnl >= 0 ? C.green : C.red}
        />
        <StatBlock
          label={`Heavy days (>${median_count} trades)`}
          rows={[
            ["Days", heavy_days.count],
            ["Avg P&L", fmt$(heavy_days.avg_pnl)],
          ]}
          accentColor={heavy_days.avg_pnl >= 0 ? C.green : C.red}
        />
      </div>

      <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
        Correlation between trade count and daily P&L: <strong style={{
          color: correlation < -0.25 ? C.red : correlation > 0.25 ? C.green : C.text,
          fontFamily: "monospace",
        }}>{correlation.toFixed(3)}</strong>
        {correlation < -0.25 && " — heavier days tend to lose money"}
        {correlation > 0.25 && " — heavier days tend to make money"}
        {correlation >= -0.25 && correlation <= 0.25 && " — no clear relationship"}
      </div>

      <Advice>
        {correlation < -0.25
          ? `On days with more than ${median_count} trades, your average P&L is ${fmt$(heavy_days.avg_pnl)} vs ${fmt$(light_days.avg_pnl)} on lighter days. This is a strong signal you should cap daily trade count.`
          : correlation > 0.25
          ? "Your edge scales with volume — heavier days actually perform better. Keep going."
          : "No strong overtrading pattern. Your performance is roughly independent of trade count."}
      </Advice>
    </div>
  );
}

function SizeAdherenceDetails({ details }) {
  const { max_size, violation_count, compliant_count, violation_pnl, violation_examples } = details;
  return (
    <div>
      <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <StatBlock
          label={`Within size limit (≤${max_size})`}
          rows={[["Trades", compliant_count]]}
          accentColor={C.green}
        />
        <StatBlock
          label={`Over size limit (>${max_size})`}
          rows={[
            ["Trades", violation_count],
            ["Net P&L", fmt$(violation_pnl)],
          ]}
          accentColor={violation_count === 0 ? C.green : C.red}
        />
      </div>

      {violation_examples?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
            Size violations (sample)
          </div>
          <div className="fos-scroll-x">
            {violation_examples.map((v, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "90px 60px 1fr 60px 80px",
                gap: 8, padding: "4px 8px", fontSize: 11, fontFamily: "monospace",
                background: "rgba(0,0,0,0.25)", borderRadius: 4, marginBottom: 2,
                minWidth: 360,
              }}>
                <span style={{ color: C.muted }}>{v.date}</span>
                <span style={{ color: C.sub }}>{v.time?.slice(0, 5)}</span>
                <span style={{ color: C.text }}>{v.symbol}</span>
                <span style={{ color: C.red, textAlign: "right" }}>size {v.size}</span>
                <span style={{ color: v.net >= 0 ? C.green : C.red, textAlign: "right" }}>
                  {v.net >= 0 ? "+" : "-"}${Math.abs(v.net).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Advice>
        {violation_count === 0
          ? `Perfect size discipline across ${compliant_count} trades. This is the easiest discipline to maintain and the hardest one to recover from breaking.`
          : `When you exceeded your max size, the trades collectively ${violation_pnl >= 0 ? "made" : "lost"} ${fmt$(Math.abs(violation_pnl))}. Even if violations win on average, they create variance that can blow accounts.`}
      </Advice>
    </div>
  );
}

function DrawdownDetails({ details }) {
  const { max_dd, total_days, breaches, close_calls, worst_days, all_days } = details;
  if (!all_days || all_days.length === 0) return <Empty msg="No days to analyze yet." />;

  return (
    <div>
      <div className="fos-grid-stats-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
        <StatBlock label="Total days" rows={[["", total_days]]} accentColor={C.text} />
        <StatBlock
          label="Breaches"
          rows={[["", breaches.length]]}
          accentColor={breaches.length === 0 ? C.green : C.red}
        />
        <StatBlock
          label="Close calls (≥75%)"
          rows={[["", close_calls.length]]}
          accentColor={close_calls.length === 0 ? C.green : C.yellow}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
          Top 5 worst days (% of $${max_dd} daily limit)
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {worst_days.map((d, i) => {
            const widthPct = Math.min(100, d.pct_of_limit * 100);
            const color = d.breached ? C.red : d.pct_of_limit >= 0.75 ? C.yellow : C.green;
            return (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "90px 1fr 100px",
                gap: 8, alignItems: "center", fontSize: 11,
              }}>
                <span style={{ color: C.muted, fontFamily: "monospace" }}>{d.date}</span>
                <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${widthPct}%`, height: "100%", background: color }} />
                </div>
                <span style={{
                  color, fontFamily: "monospace", textAlign: "right", fontWeight: 600,
                }}>
                  ${d.drawdown_used.toFixed(0)} ({(d.pct_of_limit * 100).toFixed(0)}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <Advice>
        {breaches.length > 0
          ? `${breaches.length} day${breaches.length === 1 ? "" : "s"} exceeded your $${max_dd} daily limit. In a live prop account, each one would have blown the account.`
          : close_calls.length > 0
          ? `${close_calls.length} day${close_calls.length === 1 ? "" : "s"} used 75%+ of your daily limit. Consider hard-stopping at $${Math.round(max_dd * 0.6)} to give yourself buffer.`
          : `Strong discipline — none of your ${total_days} days came within 75% of your daily limit.`}
      </Advice>
    </div>
  );
}

function StopAdherenceDetails({ details, isInsufficient }) {
  if (isInsufficient) {
    return <NeedsData msg="Fill in stop_loss when logging trades to unlock this analysis." />;
  }
  const { total_losses, blown_count, adherence_rate, total_overshoot_dollars, blown_examples } = details;
  return (
    <div>
      <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <StatBlock
          label="Stop adherence rate"
          rows={[
            ["Stops honored", `${(adherence_rate * 100).toFixed(0)}%`],
            ["Total losses", total_losses],
          ]}
          accentColor={adherence_rate >= 0.9 ? C.green : adherence_rate >= 0.7 ? C.yellow : C.red}
        />
        <StatBlock
          label="Blown stops"
          rows={[
            ["Count", blown_count],
            ["Cost (est.)", fmt$(-total_overshoot_dollars)],
          ]}
          accentColor={blown_count === 0 ? C.green : C.red}
        />
      </div>

      {blown_examples?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
            Recent blown stops (sample)
          </div>
          <div className="fos-scroll-x">
            {blown_examples.map((ex, i) => (
              <div key={i} style={{
                display: "grid",
                gridTemplateColumns: "90px 1fr 60px 80px",
                gap: 8, padding: "4px 8px", fontSize: 11, fontFamily: "monospace",
                background: "rgba(0,0,0,0.25)", borderRadius: 4, marginBottom: 2,
                minWidth: 320,
              }}>
                <span style={{ color: C.muted }}>{ex.date}</span>
                <span style={{ color: C.text }}>{ex.symbol} stop {ex.stop} → exit {ex.exit}</span>
                <span style={{ color: C.red, textAlign: "right" }}>+{ex.overshoot_pts}p</span>
                <span style={{ color: C.red, textAlign: "right" }}>{fmt$(ex.net)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Advice>
        {blown_count === 0
          ? `Perfect stop discipline across ${total_losses} losing trades. This is the elite habit — most traders break stops 20-40% of the time.`
          : `Blown stops cost approximately ${fmt$(total_overshoot_dollars)} extra beyond intended losses. The fix isn't a better strategy — it's a hard rule: exit at the stop, no exceptions.`}
      </Advice>
    </div>
  );
}

function RMultipleDetails({ details, isInsufficient }) {
  if (isInsufficient) {
    return <NeedsData msg="Fill in stop_loss when logging trades to compute risk multiples." />;
  }
  const { avg_win_r, avg_loss_r, ratio, is_healthy, buckets } = details;
  const bucketEntries = Object.entries(buckets);
  const maxBucket = Math.max(...bucketEntries.map(([, v]) => v), 1);

  return (
    <div>
      <div className="fos-grid-stats-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
        <StatBlock label="Avg winner" rows={[["", `+${avg_win_r.toFixed(2)}R`]]} accentColor={C.green} />
        <StatBlock label="Avg loser" rows={[["", `${avg_loss_r.toFixed(2)}R`]]} accentColor={C.red} />
        <StatBlock
          label="Win/Loss R ratio"
          rows={[["", ratio?.toFixed(2) || "—"]]}
          accentColor={is_healthy ? C.green : C.yellow}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
          R-multiple distribution
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {bucketEntries.map(([label, count]) => {
            const widthPct = (count / maxBucket) * 100;
            const isNeg = label.includes("-");
            return (
              <div key={label} style={{
                display: "grid",
                gridTemplateColumns: "100px 1fr 40px",
                gap: 8, alignItems: "center", fontSize: 11,
              }}>
                <span style={{ color: isNeg ? C.red : C.green, fontFamily: "monospace" }}>{label}</span>
                <div style={{ height: 8, background: C.border, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${widthPct}%`, height: "100%", background: isNeg ? C.red : C.green }} />
                </div>
                <span style={{ color: C.muted, textAlign: "right", fontFamily: "monospace" }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      <Advice>
        {is_healthy
          ? `Healthy R-multiple distribution. Avg winner is ${ratio?.toFixed(2)}× your avg loser — you're letting winners run.`
          : `Your avg winner is only ${ratio?.toFixed(2)}× your avg loser (target is ≥1.5). You're either cutting winners short or letting losers run beyond 1R.`}
      </Advice>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI
// ─────────────────────────────────────────────────────────────────────────────

function StatBlock({ label, rows, accentColor }) {
  return (
    <div style={{ padding: 12, background: "rgba(0,0,0,0.2)", borderRadius: 6, border: `1px solid ${accentColor}33` }}>
      <div style={{ fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", color: accentColor, fontWeight: 700, marginBottom: 8 }}>
        {label}
      </div>
      <table style={{ width: "100%", fontSize: 12 }}>
        <tbody>
          {rows.map(([k, v], i) => (
            <tr key={i}>
              {k && <td style={{ color: C.muted, padding: "2px 0" }}>{k}</td>}
              <td style={{ color: C.text, textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Advice({ children }) {
  return (
    <div style={{
      padding: 12, background: C.blue + "10", borderRadius: 6,
      border: `1px solid ${C.blue}33`, fontSize: 12, color: C.sub, lineHeight: 1.6,
    }}>
      <strong style={{ color: C.blue }}>What to do: </strong>{children}
    </div>
  );
}

function Empty({ msg }) {
  return <div style={{ color: C.muted, fontSize: 12, fontStyle: "italic" }}>{msg}</div>;
}

function NeedsData({ msg }) {
  return (
    <div style={{
      padding: 12, background: C.muted + "11", borderRadius: 6,
      border: `1px dashed ${C.muted}55`, fontSize: 12, color: C.muted, lineHeight: 1.6,
    }}>
      {msg}
    </div>
  );
}

function Disclaimer() {
  return (
    <div style={{ marginTop: 18, padding: 12, background: "rgba(0,0,0,0.2)", border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
      <strong style={{ color: C.sub }}>How "cost" is calculated.</strong> Overtrading cost is the gap between
      light-day and heavy-day average P&L, multiplied by heavy days. Size violation cost is the difference
      between actual and counterfactual outcomes scaled to the allowed size. Drawdown breach cost is the
      excess loss past the daily limit. Stop overshoot cost is the proportional dollar loss beyond the
      intended stop. These are estimates that surface where discipline gaps are most expensive — not
      promises of future performance.
    </div>
  );
}
